import React, { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { X } from "lucide-react";
import { toast } from "sonner";
import {
  useUpdateDeployedAgentRuntimeMutation,
  useGetLlmModelAvailabilityQuery,
  useSetDeployedAgentPromptMutation,
  useGetPromptsQuery,
} from "@/store/admin_api";
import PromptVersionPicker from "./PromptVersionPicker";
import SkillsPicker from "./SkillsPicker";
import { useGetAiVoicesQuery, useGetAiOutcomesQuery } from "@/store/rest_api";
import VoicePreviewButton from "@/components/ui/voice-preview-button";
import {
  AI_LLM_PROVIDERS,
  AI_STT_PROVIDERS,
  AI_VOICE_ORCHESTRATORS,
  AI_VOICE_PROVIDERS,
  AVAILABLE_AI_TOOLS,
  getLlmModelsForProvider,
  getSttModelsForProvider,
  isLlmModelDeprecated,
  isToolEnabled,
  toggleAgentTool,
  REALTIME_VOICES_OPENAI,
} from "@/model/interface/AiAgent";

export const VOICE_PROVIDER_UNSET = "__inherit__";
export const VOICE_UNSET = "__inherit__";
export const LLM_PROVIDER_UNSET = "__auto__";

const LLM_MODEL_UNSET = "__default__";
const STT_MODEL_UNSET = "__default__";
const STT_PROVIDER_UNSET = "__default__";
const REALTIME_VOICE_UNSET = "__default__";
const DISCLOSURE_INHERIT = "__inherit__";

/** Assign a shared-library prompt version to this agent (or inherit the template default). */
function PromptAssignment({
  agentId,
  promptVersionId,
  promptVersionLabel,
}: {
  agentId: string;
  promptVersionId?: string | null;
  promptVersionLabel?: string | null;
}) {
  const [setPrompt, { isLoading: isSaving }] = useSetDeployedAgentPromptMutation();

  const assign = async (versionId: string | null) => {
    try {
      await setPrompt({ agentId, promptVersionId: versionId }).unwrap();
      toast.success(versionId ? "Prompt assigned" : "Reset to template default");
    } catch (err: any) {
      toast.error(err?.data?.detail || "Failed to update prompt");
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Prompt</p>
      <PromptVersionPicker
        currentVersionId={promptVersionId}
        currentVersionLabel={promptVersionLabel}
        onAssign={assign}
        isSaving={isSaving}
        inheritLabel="Using the template's default prompt. Pick a version to override it."
        assignedLabel="A specific prompt version is assigned to this agent."
      />
    </div>
  );
}

export default function AgentConfigPanel({
  agentId,
  orchestrator,
  voiceProvider,
  voiceId,
  llmProvider,
  llmModel,
  customConfig,
  promptVersionId,
  promptVersionLabel,
  tools,
  declaredOutcomes,
  skills,
  templateSkills,
  greeting,
  closingMessage,
}: {
  agentId: string;
  orchestrator: string;
  voiceProvider: string | null;
  voiceId: string | null;
  llmProvider: string | null;
  llmModel: string | null;
  customConfig?: Record<string, any> | null;
  promptVersionId?: string | null;
  promptVersionLabel?: string | null;
  tools?: Record<string, any> | null;
  declaredOutcomes?: string[] | null;
  skills?: string[] | null;
  templateSkills?: string[] | null;
  greeting?: string | null;
  closingMessage?: string | null;
}) {
  const [updateRuntime] = useUpdateDeployedAgentRuntimeMutation();
  const { data: voices } = useGetAiVoicesQuery();
  const { data: llmAvailability } = useGetLlmModelAvailabilityQuery();
  const { data: availableOutcomes } = useGetAiOutcomesQuery();
  const { data: skillPage } = useGetPromptsQuery({ kind: "SKILL", size: 200 });
  const { data: archivedSkillPage } = useGetPromptsQuery({ kind: "SKILL", archived: true, size: 200 });
  const knownSkills = [...(skillPage?.items ?? []), ...(archivedSkillPage?.items ?? [])];
  const globalSkillIds = (skillPage?.items ?? [])
    .filter((skill) => skill.skillAudience === "GLOBAL")
    .map((skill) => skill.id);

  // Admin-managed tools + outcomes (moved off the org page). Seeded from the resolved (effective)
  // values so the panel reflects what the agent inherits from its template until overridden here.
  const [enabledTools, setEnabledTools] = useState<Record<string, any>>(tools ?? {});
  const [outcomes, setOutcomes] = useState<string[]>(declaredOutcomes ?? []);
  // Skill prompt ids, in the order their text is appended to the system prompt.
  const [selectedSkills, setSelectedSkills] = useState<string[]>(skills ?? []);
  const effectiveSkillIds = [...globalSkillIds, ...(templateSkills ?? []), ...selectedSkills].filter(
    (skillId, index, allSkillIds) => allSkillIds.indexOf(skillId) === index,
  );
  // Seeded from the agent's own override only (not the resolved value), so leaving these blank
  // keeps inheriting the template rather than silently pinning the template's text onto the agent.
  const [greetingText, setGreetingText] = useState<string>(greeting ?? "");
  const [closingText, setClosingText] = useState<string>(closingMessage ?? "");

  const currentStability: number = (customConfig?.voiceStability as number | undefined) ?? 0.5;
  const currentSimilarity: number = (customConfig?.voiceSimilarityBoost as number | undefined) ?? 0.75;
  const currentDisclosure = customConfig?.aiDisclosureEnabled;
  // Silence handling — stored as ms in config, displayed as seconds in the UI
  const currentSilencePromptSec = customConfig?.silencePromptTimeoutMs != null
    ? Math.round((customConfig.silencePromptTimeoutMs as number) / 1000) : undefined;
  const currentSilenceHangupSec = customConfig?.silenceHangupTimeoutMs != null
    ? Math.round((customConfig.silenceHangupTimeoutMs as number) / 1000) : undefined;
  const currentSilencePhrase = (customConfig?.silencePromptPhrase as string | undefined) ?? "";
  const currentWaitPhraseSec = customConfig?.waitPhraseTimeoutMs != null
    ? Math.round((customConfig.waitPhraseTimeoutMs as number) / 1000) : undefined;

  const currentSttModel = (customConfig?.sttModel as string | undefined) ?? undefined;
  const currentSttProvider = (customConfig?.sttProvider as string | undefined) ?? undefined;
  const currentRealtimeVoice = (customConfig?.realtimeVoice as string | undefined) ?? undefined;
  const currentTransferNumber = (customConfig?.transferNumber as string | undefined) ?? "";

  const [stability, setStability] = useState<number>(currentStability);
  const [similarity, setSimilarity] = useState<number>(currentSimilarity);
  const [selectedModel, setSelectedModel] = useState<string>(llmModel ?? LLM_MODEL_UNSET);
  const [selectedSttModel, setSelectedSttModel] = useState<string>(currentSttModel ?? STT_MODEL_UNSET);
  const [selectedSttProvider, setSelectedSttProvider] = useState<string>(currentSttProvider ?? STT_PROVIDER_UNSET);
  const [selectedRealtimeVoice, setSelectedRealtimeVoice] = useState<string>(currentRealtimeVoice ?? REALTIME_VOICE_UNSET);
  const [disclosureValue, setDisclosureValue] = useState<string>(
    currentDisclosure === null || currentDisclosure === undefined ? DISCLOSURE_INHERIT : String(currentDisclosure)
  );
  const [silencePromptSec, setSilencePromptSec] = useState<string>(
    currentSilencePromptSec !== undefined ? String(currentSilencePromptSec) : ""
  );
  const [silenceHangupSec, setSilenceHangupSec] = useState<string>(
    currentSilenceHangupSec !== undefined ? String(currentSilenceHangupSec) : ""
  );
  const [silencePhrase, setSilencePhrase] = useState<string>(currentSilencePhrase);
  const [waitPhraseSec, setWaitPhraseSec] = useState<string>(
    currentWaitPhraseSec !== undefined ? String(currentWaitPhraseSec) : ""
  );
  const [transferNumber, setTransferNumber] = useState<string>(currentTransferNumber);
  const [selectedVoiceProvider, setSelectedVoiceProvider] = useState<string>(voiceProvider ?? VOICE_PROVIDER_UNSET);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(voiceId ?? VOICE_UNSET);
  const [selectedLlmProvider, setSelectedLlmProvider] = useState<string>(llmProvider ?? LLM_PROVIDER_UNSET);

  // Conversation tuning (advanced). Durations stored in ms; min-call shown in seconds, max in seconds.
  const ccNumStr = (key: string): string => (customConfig?.[key] != null ? String(customConfig[key]) : "");
  const currentMinCallSec = customConfig?.minCallDurationMs != null
    ? String(Math.round((customConfig.minCallDurationMs as number) / 1000)) : "";
  const [temperature, setTemperature] = useState<string>(ccNumStr("temperature"));
  const [endpointingMs, setEndpointingMs] = useState<string>(ccNumStr("endpointingMs"));
  const [turnHoldMs, setTurnHoldMs] = useState<string>(ccNumStr("turnHoldMs"));
  const [minCallSec, setMinCallSec] = useState<string>(currentMinCallSec);
  const [maxDurationSec, setMaxDurationSec] = useState<string>(ccNumStr("maxDurationSeconds"));

  const [selectedOrchestrator, setSelectedOrchestrator] = useState<string>(orchestrator);
  const [saving, setSaving] = useState(false);

  const effectiveVoiceProvider = selectedVoiceProvider === VOICE_PROVIDER_UNSET ? "ELEVENLABS" : selectedVoiceProvider;
  const isNative = selectedOrchestrator === "NATIVE";
  const isVapi = selectedOrchestrator === "VAPI";
  const isRealtime = selectedOrchestrator === "OPENAI_REALTIME";
  const effectiveLlmProvider = selectedLlmProvider === LLM_PROVIDER_UNSET ? null : selectedLlmProvider;
  // With no provider chosen the two orchestrators resolve differently: Vapi falls back to OpenAI,
  // while Native walks the platform provider order (Groq first). So we can pre-fill the model list
  // for Vapi, but on Native we'd be offering OpenAI ids to an agent about to run on Groq.
  const availableModels = effectiveLlmProvider
    ? getLlmModelsForProvider(effectiveLlmProvider)
    : isVapi
      ? getLlmModelsForProvider("OPENAI")
      : [];

  const buildCustomConfig = (voiceStability: number | null, voiceSimilarityBoost: number | null) => ({
    voiceStability,
    voiceSimilarityBoost,
    // STT provider + model — null means "use platform default"
    sttProvider: selectedSttProvider === STT_PROVIDER_UNSET ? null : selectedSttProvider,
    sttModel: selectedSttModel === STT_MODEL_UNSET ? null : selectedSttModel,
    // OpenAI Realtime voice — null means "use platform default"
    realtimeVoice: selectedRealtimeVoice === REALTIME_VOICE_UNSET ? null : selectedRealtimeVoice,
    aiDisclosureEnabled: disclosureValue === DISCLOSURE_INHERIT ? null : disclosureValue === "true",
    // Convert seconds → ms; null means "use platform default"
    silencePromptTimeoutMs: silencePromptSec.trim() ? parseInt(silencePromptSec) * 1000 : null,
    silenceHangupTimeoutMs: silenceHangupSec.trim() ? parseInt(silenceHangupSec) * 1000 : null,
    silencePromptPhrase: silencePhrase.trim() || null,
    waitPhraseTimeoutMs: waitPhraseSec.trim() ? parseInt(waitPhraseSec) * 1000 : null,
    // Transfer destination — null means transfer_call is gated off (agent offers a callback instead).
    transferNumber: transferNumber.trim() || null,
    // Conversation tuning — null = use platform default; min-call converted seconds → ms.
    temperature: temperature.trim() ? parseFloat(temperature) : null,
    endpointingMs: endpointingMs.trim() ? parseInt(endpointingMs) : null,
    turnHoldMs: turnHoldMs.trim() ? parseInt(turnHoldMs) : null,
    minCallDurationMs: minCallSec.trim() ? parseInt(minCallSec) * 1000 : null,
    maxDurationSeconds: maxDurationSec.trim() ? parseInt(maxDurationSec) : null,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateRuntime({
        agentId,
        orchestrator: selectedOrchestrator,
        voiceProvider: selectedVoiceProvider === VOICE_PROVIDER_UNSET ? null : selectedVoiceProvider,
        voiceId: selectedVoiceId === VOICE_UNSET ? null : selectedVoiceId,
        llmProvider: effectiveLlmProvider,
        llmModel: selectedModel === LLM_MODEL_UNSET ? null : selectedModel,
        customConfig: buildCustomConfig(stability, similarity),
        tools: enabledTools,
        declaredOutcomes: outcomes,
        skills: selectedSkills,
        greeting: greetingText,
        closingMessage: closingText,
      }).unwrap();
      toast.success("Agent config saved");
    } catch (err: any) {
      toast.error(err?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleResetVoice = async () => {
    setSaving(true);
    try {
      await updateRuntime({
        agentId,
        orchestrator: selectedOrchestrator,
        voiceProvider: selectedVoiceProvider === VOICE_PROVIDER_UNSET ? null : selectedVoiceProvider,
        voiceId: selectedVoiceId === VOICE_UNSET ? null : selectedVoiceId,
        llmProvider: effectiveLlmProvider,
        llmModel: selectedModel === LLM_MODEL_UNSET ? null : selectedModel,
        customConfig: buildCustomConfig(null, null),
        tools: enabledTools,
        declaredOutcomes: outcomes,
        skills: selectedSkills,
        greeting: greetingText,
        closingMessage: closingText,
      }).unwrap();
      setStability(0.5);
      setSimilarity(0.75);
      toast.success("Voice quality reset to defaults");
    } catch (err: any) {
      toast.error(err?.data?.detail || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 border-t px-6 py-4 space-y-6">

      {/* Prompt (shared library) */}
      <PromptAssignment agentId={agentId} promptVersionId={promptVersionId} promptVersionLabel={promptVersionLabel} />

      {/* Greeting + closing */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Greeting &amp; Closing</p>
        <div className="space-y-1.5 max-w-lg">
          <label className="text-sm font-medium">Greeting</label>
          <input
            type="text"
            placeholder="Leave empty to inherit the template's greeting"
            value={greetingText}
            onChange={(e) => setGreetingText(e.target.value)}
            className="w-full h-9 rounded-md border bg-background px-3 text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Spoken verbatim as the first thing on the call. With AI disclosure enabled this is the
            agent's first message, so it must state that it's an AI.
          </p>
        </div>
        <div className="space-y-1.5 max-w-lg">
          <label className="text-sm font-medium">Closing message</label>
          <textarea
            rows={2}
            placeholder="Leave empty to inherit the template's closing message"
            value={closingText}
            onChange={(e) => setClosingText(e.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Not read out word-for-word — injected as guidance so the agent works it into its
            goodbye. Skipped when a call ends badly.
          </p>
        </div>
      </div>

      {/* AI Behaviour */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">AI Behaviour</p>
        <div className="space-y-1.5 max-w-sm">
          <label className="text-sm font-medium">AI Disclosure</label>
          <Select value={disclosureValue} onValueChange={setDisclosureValue}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={DISCLOSURE_INHERIT}>Inherit from template / platform</SelectItem>
              <SelectItem value="true">Enabled — agent admits it's AI when asked</SelectItem>
              <SelectItem value="false">Disabled — agent maintains human persona</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Controls how the agent responds if a caller asks whether they're talking to a real person.
          </p>
        </div>
      </div>

      {/* Runtime */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Runtime</p>
        <div className="space-y-1.5 max-w-sm">
          <label className="text-sm font-medium">Orchestrator</label>
          <Select value={selectedOrchestrator} onValueChange={setSelectedOrchestrator}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AI_VOICE_ORCHESTRATORS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            What runs the call for this agent, regardless of its template. The model, speech-to-text and
            voice settings below follow the choice made here.
          </p>
        </div>
      </div>

      {/* Voice */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Voice</p>
        <div className="space-y-1.5 max-w-sm">
          <label className="text-sm font-medium">Provider</label>
          <Select
            value={selectedVoiceProvider}
            onValueChange={(v) => { setSelectedVoiceProvider(v); setSelectedVoiceId(VOICE_UNSET); }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={VOICE_PROVIDER_UNSET}>Inherit (ElevenLabs)</SelectItem>
              {AI_VOICE_PROVIDERS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            The TTS provider for this agent. Changing it clears the selected voice.
          </p>
        </div>
        <div className="space-y-1.5 max-w-sm">
          <label className="text-sm font-medium">Voice</label>
          <Select value={selectedVoiceId} onValueChange={setSelectedVoiceId}>
            <SelectTrigger className="[&_.voice-extra]:hidden">
              <SelectValue placeholder="Inherit from template" />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4} className="max-h-[300px]">
              <SelectItem value={VOICE_UNSET}>Inherit from template</SelectItem>
              {voices
                ?.filter((voice) => (voice.provider || "ELEVENLABS") === effectiveVoiceProvider)
                .map((voice) => (
                  <SelectItem key={voice.id} value={voice.id} className="pr-2">
                    <span className="flex items-center justify-between gap-2 w-full">
                      <span className="flex flex-col min-w-0">
                        <span className="truncate">
                          {voice.name}
                          {voice.gender ? ` · ${voice.gender}` : ""}
                          {voice.accent ? ` · ${voice.accent}` : ""}
                        </span>
                        {voice.description ? (
                          <span className="voice-extra text-xs text-muted-foreground truncate">{voice.description}</span>
                        ) : null}
                      </span>
                      <VoicePreviewButton previewUrl={voice.previewUrl} label={voice.name} className="voice-extra h-6 w-6 shrink-0" />
                    </span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Voice used for text-to-speech — showing {effectiveVoiceProvider} voices.
          </p>
        </div>
      </div>

      {/* LLM Provider + Model (not used by the realtime orchestrator) */}
      {(isNative || isVapi) && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">LLM</p>
          <div className="space-y-1.5 max-w-sm">
            <label className="text-sm font-medium">Provider</label>
            <Select
              value={selectedLlmProvider}
              onValueChange={(v) => { setSelectedLlmProvider(v); setSelectedModel(LLM_MODEL_UNSET); }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Platform default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={LLM_PROVIDER_UNSET}>Platform default (auto)</SelectItem>
                {AI_LLM_PROVIDERS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Changing the provider resets the model below.</p>
          </div>
          {availableModels.length > 0 ? (
            <div className="space-y-1.5 max-w-sm">
              <label className="text-sm font-medium">Model</label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger>
                  <SelectValue placeholder="Platform default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={LLM_MODEL_UNSET}>Platform default</SelectItem>
                  {availableModels.map((m) => {
                    const deprecated = isLlmModelDeprecated(effectiveLlmProvider, m.value, llmAvailability);
                    return (
                      <SelectItem key={m.value} value={m.value}>
                        <div className="flex flex-col">
                          <span>
                            {m.label}
                            {deprecated && <span className="ml-2 text-xs text-amber-600">⚠ no longer offered by provider</span>}
                          </span>
                          {m.description && (
                            <span className="text-xs text-muted-foreground">{m.description}</span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Overrides the platform-wide default for this agent only.</p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Pick a provider above to choose a specific model.</p>
          )}
        </div>
      )}

      {/* Speech-to-Text (Native orchestrator only) */}
      {isNative && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Speech-to-Text</p>
          <div className="space-y-1.5 max-w-sm">
            <label className="text-sm font-medium">Provider</label>
            <Select
              value={selectedSttProvider}
              onValueChange={(v) => { setSelectedSttProvider(v); setSelectedSttModel(STT_MODEL_UNSET); }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Platform default (Deepgram)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={STT_PROVIDER_UNSET}>Platform default (Deepgram)</SelectItem>
                {AI_STT_PROVIDERS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Which vendor transcribes the caller. Changing the provider resets the model below.
            </p>
          </div>
          <div className="space-y-1.5 max-w-sm">
            <label className="text-sm font-medium">Model</label>
            <Select value={selectedSttModel} onValueChange={setSelectedSttModel}>
              <SelectTrigger>
                <SelectValue placeholder="Provider default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={STT_MODEL_UNSET}>Provider default</SelectItem>
                {getSttModelsForProvider(
                  selectedSttProvider === STT_PROVIDER_UNSET ? "DEEPGRAM" : selectedSttProvider
                ).map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <div className="flex flex-col">
                      <span>{m.label}</span>
                      <span className="text-xs text-muted-foreground">{m.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              STT model within the chosen provider. Leave on default unless you're testing accuracy/latency.
            </p>
          </div>
        </div>
      )}

      {/* Voice (OpenAI Realtime orchestrator only) */}
      {isRealtime && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Realtime Voice</p>
          <div className="space-y-1.5 max-w-sm">
            <label className="text-sm font-medium">OpenAI Voice</label>
            <Select value={selectedRealtimeVoice} onValueChange={setSelectedRealtimeVoice}>
              <SelectTrigger>
                <SelectValue placeholder="Platform default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={REALTIME_VOICE_UNSET}>Platform default</SelectItem>
                {REALTIME_VOICES_OPENAI.map((v) => (
                  <SelectItem key={v.value} value={v.value}>
                    <div className="flex flex-col">
                      <span>{v.label}</span>
                      <span className="text-xs text-muted-foreground">{v.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">The OpenAI voice for this agent. marin and cedar are the newest, most natural.</p>
          </div>
        </div>
      )}

      {/* Silence Handling (Native only) */}
      {isNative && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Silence Handling</p>
          <p className="text-xs text-muted-foreground -mt-1">
            Controls what happens when the caller stops responding. Leave blank to use platform defaults (7s re-prompt, 30s hangup).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Re-prompt delay (seconds)</label>
              <input
                type="number" min={1} max={60} placeholder="7 (default)"
                value={silencePromptSec}
                onChange={(e) => setSilencePromptSec(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              />
              <p className="text-xs text-muted-foreground">Seconds of silence after agent speaks before asking "Are you still there?"</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Hangup timeout (seconds)</label>
              <input
                type="number" min={5} max={300} placeholder="30 (default)"
                value={silenceHangupSec}
                onChange={(e) => setSilenceHangupSec(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              />
              <p className="text-xs text-muted-foreground">Seconds of total user silence before ending the call with no-response outcome.</p>
            </div>
          </div>
          <div className="space-y-1.5 max-w-lg">
            <label className="text-sm font-medium">Re-prompt phrase</label>
            <input
              type="text" placeholder="Are you still there? (default)"
              value={silencePhrase}
              onChange={(e) => setSilencePhrase(e.target.value)}
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
            />
            <p className="text-xs text-muted-foreground">What the agent says when the caller has been silent too long.</p>
          </div>
          <div className="space-y-1.5 max-w-lg">
            <label className="text-sm font-medium">Wait-phrase hold window (seconds)</label>
            <input
              type="number" min={5} max={120} placeholder="20 (default)"
              value={waitPhraseSec}
              onChange={(e) => setWaitPhraseSec(e.target.value)}
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              How long to suppress the "Are you still there?" prompt after the caller says "hold on" or "give me a sec". Extend for agents where callers often need to look things up.
            </p>
          </div>
        </div>
      )}

      {/* Conversation tuning (advanced) — Native only */}
      {isNative && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Conversation tuning (advanced)</p>
          <p className="text-xs text-muted-foreground -mt-1">
            Fine-tune how this agent listens, paces, and talks. Leave blank to use the platform default.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-lg">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Temperature (0–1)</label>
              <input type="number" step="0.1" min={0} max={1} placeholder="0.7 (default)"
                value={temperature} onChange={(e) => setTemperature(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm" />
              <p className="text-xs text-muted-foreground">Response randomness. Lower = focused/consistent; higher = varied/creative.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Endpointing (ms)</label>
              <input type="number" min={50} max={3000} placeholder="200 (default)"
                value={endpointingMs} onChange={(e) => setEndpointingMs(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm" />
              <p className="text-xs text-muted-foreground">Caller pause before the agent assumes they've finished. Lower = snappier but more cut-offs.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Continuation hold (ms)</label>
              <input type="number" min={0} max={3000} placeholder="400 (default)"
                value={turnHoldMs} onChange={(e) => setTurnHoldMs(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm" />
              <p className="text-xs text-muted-foreground">Extra wait for a trailing continuation so the agent doesn't answer mid-thought.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Minimum call duration (seconds)</label>
              <input type="number" min={0} max={600} placeholder="45 (default)"
                value={minCallSec} onChange={(e) => setMinCallSec(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm" />
              <p className="text-xs text-muted-foreground">Blocks auto-hangup before this unless the caller clearly ends or the goal is met.</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Maximum call duration (seconds)</label>
              <input type="number" min={0} max={7200} placeholder="no cap"
                value={maxDurationSec} onChange={(e) => setMaxDurationSec(e.target.value)}
                className="w-full h-9 rounded-md border bg-background px-3 text-sm" />
              <p className="text-xs text-muted-foreground">Hard cap on call length as a safety stop. Blank = no cap.</p>
            </div>
          </div>
        </div>
      )}

      {/* Tools */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Enabled Tools</p>
        <div className="space-y-2 rounded-md border bg-background p-3 max-w-lg">
          {AVAILABLE_AI_TOOLS.map((tool) => (
            <label key={tool.name} className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={isToolEnabled(enabledTools, tool.name)}
                onCheckedChange={(checked) =>
                  setEnabledTools(toggleAgentTool(enabledTools, tool.name, checked === true))
                }
              />
              <span className="flex flex-col">
                <span className="text-sm font-medium">{tool.label}</span>
                <span className="text-xs text-muted-foreground">{tool.description}</span>
              </span>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Tools this agent can call. <code>transfer_call</code> also needs a transfer number below.
          <code> set_outcome</code> only works with outcomes declared here.
        </p>
      </div>

      {/* Declared Outcomes */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Declared Outcomes</p>
        <div className="max-w-sm">
          <Select
            value=""
            onValueChange={(o) => { if (!outcomes.includes(o)) setOutcomes([...outcomes, o]); }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Add outcome..." />
            </SelectTrigger>
            <SelectContent>
              {(availableOutcomes ?? [])
                .filter((o) => !outcomes.includes(o))
                .map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {outcomes.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {outcomes.map((o) => (
              // The panel sits on a muted background, so `secondary` badges (also muted) wash out —
              // use a bordered solid-background chip so outcomes stay legible in both themes.
              <Badge key={o} variant="outline" className="gap-1 bg-background border-border text-foreground">
                {o}
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={() => setOutcomes(outcomes.filter((x) => x !== o))}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          What <code>set_outcome</code> can record and what drives workflow onOutcome routing.
        </p>
      </div>

      {/* Skills — order matters, so this is an ordered list rather than chips */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Skills</p>
        <SkillsPicker
          selectedSkillIds={selectedSkills}
          onChange={setSelectedSkills}
          inheritedSkillIds={templateSkills ?? []}
          ownGroupLabel="This agent"
          ownGroupHint="only this agent gets these"
        />
        <p className="text-xs text-muted-foreground">
          Capability blocks appended to this agent's prompt, in this order. Each follows its latest
          published version. Edit the text in the Skills tab.
        </p>

        {/* Derived, never configured: the squad shape falls out of which skills are call stages,
            so an admin can see the consequence of a selection without reading the design doc. */}
        {isVapi && effectiveSkillIds.length > 0 && (() => {
          const chosen = effectiveSkillIds
            .map((id) => knownSkills.find((s) => s.id === id))
            .filter((s): s is NonNullable<typeof s> => Boolean(s));
          const phases = chosen.filter((s) => s.skillType === "PHASE");
          const policies = chosen.filter((s) => s.skillType !== "PHASE");
          // The split that decides how big each call stage's prompt is. A stage carries only the
          // always-on skills that can still fire once it has started, which is the whole reason
          // splitting the call up is worth anything.
          const shared = policies.filter((s) => s.skillScope !== "ROUTER_ONLY");
          const preStage = policies.filter((s) => s.skillScope === "ROUTER_ONLY");
          return (
            <div className="max-w-lg rounded-md border border-dashed p-3 text-xs text-muted-foreground">
              {phases.length === 0 ? (
                <>On Vapi this agent runs as a <strong className="text-foreground">single assistant</strong> — no
                skill is marked as a call stage.</>
              ) : (
                <>
                  <p>
                    On Vapi this agent runs as a <strong className="text-foreground">squad</strong>:
                  </p>
                  <p className="mt-1">
                    <strong className="text-foreground">Router</strong>
                    {policies.length > 0 && ` + ${policies.length} always-on skill${policies.length > 1 ? "s" : ""}`}
                  </p>
                  {phases.map((p) => (
                    <p key={p.id} className="mt-0.5">
                      → <strong className="text-foreground">{p.name}</strong>
                      {` + ${shared.length} always-on skill${shared.length === 1 ? "" : "s"}`}
                      {(p.memberTools ?? []).length > 0 && ` [${(p.memberTools ?? []).join(", ")}]`}
                    </p>
                  ))}
                  {preStage.length > 0 && (
                    <p className="mt-1.5">
                      {preStage.length} skill{preStage.length > 1 ? "s are" : " is"} marked pre-stage
                      ({preStage.map((s) => s.name).join(", ")}), so the call stages above don't carry
                      {preStage.length > 1 ? " them" : " it"} — that is what keeps a stage quick to start.
                    </p>
                  )}
                  {preStage.length === 0 && policies.length > 0 && (
                    <p className="mt-1.5 text-foreground/80">
                      Every always-on skill goes into every call stage, so the stages are no smaller
                      than the router. Mark the ones that only matter before a stage begins as
                      pre-stage.
                    </p>
                  )}
                  <p className="mt-1.5">
                    The router loses those tools so it hands over instead of doing the work itself.
                    Other orchestrators ignore the split and use all skills in one prompt.
                  </p>
                </>
              )}
            </div>
          );
        })()}
      </div>

      {/* Transfer destination */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Transfer Destination</p>
        <div className="space-y-1.5 max-w-sm">
          <label className="text-sm font-medium">Transfer-to-human number</label>
          <input
            type="tel" placeholder="+14155551234"
            value={transferNumber}
            onChange={(e) => setTransferNumber(e.target.value)}
            className="w-full h-9 rounded-md border bg-background px-3 text-sm"
          />
          <p className="text-xs text-muted-foreground">
            E.164 number the agent transfers callers to when they ask for a human. Leave empty to gate
            off the transfer_call tool — the agent then offers a callback instead.
          </p>
        </div>
      </div>

      {/* Voice Quality (ElevenLabs) */}
      <div className="space-y-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Voice Quality (ElevenLabs)</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Stability</label>
              <span className="text-sm font-mono text-muted-foreground">{stability.toFixed(2)}</span>
            </div>
            <input
              type="range" min={0} max={1} step={0.05} value={stability}
              onChange={(e) => setStability(parseFloat(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-muted accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Expressive</span><span>Stable</span>
            </div>
            <p className="text-xs text-muted-foreground">Lower = more expressive. Recommended: 0.30–0.45 for sales calls.</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Similarity Boost</label>
              <span className="text-sm font-mono text-muted-foreground">{similarity.toFixed(2)}</span>
            </div>
            <input
              type="range" min={0} max={1} step={0.05} value={similarity}
              onChange={(e) => setSimilarity(parseFloat(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-muted accent-primary"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>More variation</span><span>Stays true to voice</span>
            </div>
            <p className="text-xs text-muted-foreground">How closely output matches original voice. Recommended: 0.75–0.85.</p>
          </div>
        </div>
      </div>

      {/* Single save row */}
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={handleSave} disabled={saving}>Save</Button>
        <Button size="sm" variant="outline" onClick={handleResetVoice} disabled={saving}>Reset voice to defaults</Button>
      </div>
    </div>
  );
}
